"use client";

import { useState, useEffect } from "react";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { BookingCardCrafted, BookingCardPayload } from "@/components/booking-card/BookingCardCrafted";
import { themesCrafted } from "@/components/booking-card/themesCrafted";
import { BOOKING_THEMES, type BookingTheme } from "@/components/booking-flow/themes";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_CANCELLATION_POLICY } from "@/components/booking-shared/booking";
import { PolicyCard } from "@/components/booking-shared/PolicyCard";
import { flowThemeVars } from "@/components/booking-flow/themes";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Check, Loader2 } from "lucide-react";

// ── "Before Booking" theme keys ─────────────────────────────────────
const STANDARD_FLOW_THEME_KEYS = [
  'figgy_purple',
  'cognac',
  'modern_black',
  'plum',
  'emerald',
] as const;

// ── Theme swatch ────────────────────────────────────────────────────
function FlowThemeSwatch({ theme, selected, onClick }: {
  theme: BookingTheme;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0,
        border: selected ? `2px solid ${theme.flowAccent}` : '2px solid transparent',
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        background: 'transparent',
        padding: 0,
        position: 'relative',
        transition: 'border-color 200ms ease, box-shadow 200ms ease',
        boxShadow: selected
          ? `0 0 0 2px ${theme.flowAccent}33, 0 4px 12px rgba(0,0,0,0.12)`
          : '0 1px 4px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{
        height: 80,
        background: theme.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}>
        <span style={{
          fontFamily: theme.display,
          fontStyle: theme.displayItalic ? 'italic' : 'normal',
          fontWeight: theme.displayWeight,
          fontSize: 16,
          color: theme.fg,
          letterSpacing: '-0.01em',
        }}>
          {theme.name}
        </span>
        {selected && (
          <div style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 20,
            height: 20,
            borderRadius: 10,
            background: theme.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Check size={12} color={theme.bg} strokeWidth={2.5} />
          </div>
        )}
      </div>
      <div style={{
        height: 32,
        background: theme.flowBg || '#F4F1EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: '0 8px',
      }}>
        <div style={{
          width: 28, height: 14, borderRadius: 4,
          background: theme.flowSurface || '#FBF8F2',
          border: `1px solid ${theme.flowHairline || 'rgba(28,18,12,0.10)'}`,
        }} />
        <div style={{
          width: 28, height: 14, borderRadius: 4,
          background: theme.flowAccent, opacity: 0.7,
        }} />
        <div style={{
          width: 28, height: 14, borderRadius: 4,
          background: theme.flowSurface || '#FBF8F2',
          border: `1px solid ${theme.flowHairline || 'rgba(28,18,12,0.10)'}`,
        }} />
      </div>
    </button>
  );
}

// ── Confirmation card preview payload ────────────────────────────────
function makeCardPreviewPayload(themeKey: string, businessName: string): BookingCardPayload {
  return {
    business_name: businessName || "Your Business",
    business_instagram_handle: null,
    booking_card_theme_key: themeKey,
    client_full_name: "Alex Rivera",
    displayed_visits: 12,
    member_since_date: "2024-06-01",
    service_name: "Deep Tissue Massage",
    appointment_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    appointment_time: "14:30:00",
    location_label: null,
    public_confirmation_code: "FIGGY-PREVIEW",
  };
}

// ── Main component ───────────────────────────────────────────────────
export default function BookingPageSettings() {
  // Shared state
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Flow theme state
  const [flowThemeKey, setFlowThemeKey] = useState<string>('figgy_purple');
  const [savingFlowTheme, setSavingFlowTheme] = useState(false);

  // Card theme state
  const [selectedCardTheme, setSelectedCardTheme] = useState<string>("midnight_luxe");
  const [savedCardTheme, setSavedCardTheme] = useState<string>("midnight_luxe");
  const [isSavingCard, setIsSavingCard] = useState(false);

  // Cancellation policy state (empty string = use the generic default)
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [savedCancellationPolicy, setSavedCancellationPolicy] = useState("");
  const [isSavingPolicy, setIsSavingPolicy] = useState(false);

  // Single data load for both sections
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: business } = await supabase
          .from("business_profiles")
          .select("id, name, booking_flow_theme_key, booking_card_theme_key, cancellation_fee_policy")
          .eq("user_id", user.id)
          .maybeSingle();

        if (business) {
          setBusinessId(business.id);
          setBusinessName(business.name || "");
          setFlowThemeKey(business.booking_flow_theme_key || 'figgy_purple');
          const cardTheme = business.booking_card_theme_key || "midnight_luxe";
          setSelectedCardTheme(cardTheme);
          setSavedCardTheme(cardTheme);
          const policy = business.cancellation_fee_policy || "";
          setCancellationPolicy(policy);
          setSavedCancellationPolicy(policy);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Flow theme: optimistic save on click
  const handleFlowThemeSelect = async (key: string) => {
    if (!businessId || key === flowThemeKey) return;
    const prevKey = flowThemeKey;
    setFlowThemeKey(key);
    setSavingFlowTheme(true);

    const { error } = await supabase
      .from('business_profiles')
      .update({ booking_flow_theme_key: key })
      .eq('id', businessId);

    setSavingFlowTheme(false);
    if (error) {
      setFlowThemeKey(prevKey);
      toast.error('Failed to update theme');
    } else {
      toast.success('Theme updated');
    }
  };

  // Card theme: explicit save
  const handleCardSave = async () => {
    if (!businessId) return;
    setIsSavingCard(true);
    try {
      const { error } = await supabase
        .from("business_profiles")
        .update({ booking_card_theme_key: selectedCardTheme })
        .eq("id", businessId);

      if (error) throw error;
      setSavedCardTheme(selectedCardTheme);
      toast.success("Confirmation card saved.");
    } catch {
      toast.error("Failed to save. Please try again.");
    } finally {
      setIsSavingCard(false);
    }
  };

  // Cancellation policy: explicit save; empty stores NULL so the booking
  // page falls back to the generic copy
  const handlePolicySave = async () => {
    if (!businessId) return;
    setIsSavingPolicy(true);
    try {
      const trimmed = cancellationPolicy.trim();
      const { error } = await supabase
        .from("business_profiles")
        .update({ cancellation_fee_policy: trimmed || null })
        .eq("id", businessId);

      if (error) throw error;
      setCancellationPolicy(trimmed);
      setSavedCancellationPolicy(trimmed);
      toast.success("Cancellation policy saved.");
    } catch {
      toast.error("Failed to save. Please try again.");
    } finally {
      setIsSavingPolicy(false);
    }
  };

  const cardPreviewPayload = useMemo(
    () => makeCardPreviewPayload(selectedCardTheme, businessName),
    [selectedCardTheme, businessName]
  );

  const hasCardChanges = selectedCardTheme !== savedCardTheme;
  const hasPolicyChanges = cancellationPolicy.trim() !== savedCancellationPolicy;
  const cardThemes = Object.values(themesCrafted);
  const activeFlowTheme = BOOKING_THEMES[flowThemeKey] || BOOKING_THEMES.figgy_purple;

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          <div className="mb-8">
            <h1 className="text-2xl font-semibold">Booking Page</h1>
            <p className="text-muted-foreground">
              Customize what clients see before and after they book.
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-12">

              {/* ─── Section 1: Before Booking ─── */}
              <section>
                <div className="mb-5">
                  <h2 className="text-lg font-medium">Before Booking</h2>
                  <p className="text-sm text-muted-foreground">
                    What clients see when they visit your booking page and schedule an appointment.
                  </p>
                </div>

                <div className="mb-2">
                  <h3 className="text-sm font-medium mb-1">Theme</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Sets the colors, typography, and overall look of your booking page.
                  </p>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 12,
                }}>
                  {STANDARD_FLOW_THEME_KEYS.map((key) => {
                    const t = BOOKING_THEMES[key];
                    if (!t) return null;
                    return (
                      <FlowThemeSwatch
                        key={key}
                        theme={t}
                        selected={flowThemeKey === key}
                        onClick={() => handleFlowThemeSelect(key)}
                      />
                    );
                  })}
                </div>

                {savingFlowTheme && (
                  <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                  </p>
                )}

                {/* Cancellation policy */}
                <div className="mt-10 mb-2">
                  <h3 className="text-sm font-medium mb-1">Cancellation Policy</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Shown to clients on the booking page before they confirm.
                    Leave empty to use the standard policy.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                  <div>
                    <textarea
                      value={cancellationPolicy}
                      onChange={(e) => setCancellationPolicy(e.target.value)}
                      placeholder={DEFAULT_CANCELLATION_POLICY}
                      maxLength={500}
                      rows={5}
                      className="w-full rounded-lg border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-3">
                        <Button
                          size="sm"
                          onClick={handlePolicySave}
                          disabled={!hasPolicyChanges || isSavingPolicy}
                        >
                          {isSavingPolicy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                          Save policy
                        </Button>
                        {cancellationPolicy.trim() !== "" && (
                          <button
                            type="button"
                            onClick={() => setCancellationPolicy("")}
                            className="text-xs text-muted-foreground underline hover:text-foreground"
                          >
                            Reset to default
                          </button>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {cancellationPolicy.length}/500
                      </span>
                    </div>
                  </div>

                  {/* Live preview — the literal PolicyCard the booking
                      page renders, themed with the selected flow theme */}
                  <div style={{
                    background: activeFlowTheme.flowBg || '#F4F1EB',
                    borderRadius: 16,
                    padding: '20px 20px 22px',
                    ...flowThemeVars(activeFlowTheme),
                  } as React.CSSProperties}>
                    <div style={{
                      fontSize: 10,
                      textTransform: 'uppercase' as const,
                      letterSpacing: '0.22em',
                      fontWeight: 600,
                      color: activeFlowTheme.flowInkFaint || 'rgba(28,18,12,0.40)',
                      marginBottom: 10,
                    }}>Preview</div>
                    <PolicyCard policy={cancellationPolicy} />
                  </div>
                </div>
              </section>

              {/* ─── Section 2: After Booking ─── */}
              <section>
                <div className="mb-5">
                  <h2 className="text-lg font-medium">After Booking</h2>
                  <p className="text-sm text-muted-foreground">
                    What clients receive after their booking is confirmed.
                  </p>
                </div>

                <div className="mb-2">
                  <h3 className="text-sm font-medium mb-1">Confirmation Card</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Customize the confirmation card clients see after booking.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
                  {/* Card theme picker */}
                  <div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                      {cardThemes.map((theme) => (
                        <button
                          key={theme.key}
                          onClick={() => setSelectedCardTheme(theme.key)}
                          className={cn(
                            "relative flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all",
                            selectedCardTheme === theme.key
                              ? "border-primary shadow-sm"
                              : "border-transparent bg-white hover:border-gray-200 shadow-sm"
                          )}
                          style={{
                            backgroundColor: theme.gradientFrom,
                            color: theme.textPrimary,
                          }}
                        >
                          <div
                            className="w-4 h-4 rounded-full shrink-0 border border-black/10"
                            style={{ backgroundColor: theme.accentColor }}
                          />
                          <span className="text-sm font-medium">{theme.name}</span>
                          {selectedCardTheme === theme.key && (
                            <div className="absolute inset-0 rounded-xl ring-2 ring-primary ring-offset-1 pointer-events-none" />
                          )}
                        </button>
                      ))}
                    </div>

                    <Button
                      onClick={handleCardSave}
                      disabled={!hasCardChanges || isSavingCard}
                      className="w-full sm:w-auto"
                    >
                      {isSavingCard ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving…
                        </>
                      ) : hasCardChanges ? "Save" : "Saved"}
                    </Button>
                  </div>

                  {/* Live card preview */}
                  <div className="lg:sticky lg:top-24">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Preview</p>
                    <BookingCardCrafted
                      payload={cardPreviewPayload}
                    />
                  </div>
                </div>
              </section>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
